import { Fragment, useContext, useEffect, useMemo, useState } from 'react';
import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import { ControllerPlus, Loader, TextField } from '@components';
import { CONSTANT } from '@const';
import { LegalEntityContext, ModalContext } from '@context';
import { DeviceReadingService, DeviceService } from '@data';
import { Pagination, Skeleton, Tabs } from '@general';
import { yupResolver } from '@hookform/resolvers/yup';
import { Close, Done, Edit, Meter } from '@public';
import {
	ConnectedDeviceSummary,
	DeviceSummary,
	DeviceType,
	PageFilter,
	ReadingStatus,
} from '@types';
import {
	convertDateAndTimeToDisplayFormat,
	DeviceDisplayController,
	displayNameErrorMessage,
	displayNameRegExp,
	displayReadingValue,
	displayVolumeOrStatus,
	handlePageChange,
	hasAssessedTake,
	toSentenceCase,
} from '@utils';
import { isEmpty } from 'lodash';
import { Resolver, useForm } from 'react-hook-form';
import * as yup from 'yup';

import { NotificationToast } from '@/components';
import { AssessedTake } from '../assessedTake/AssessedTake';

import styles from './deviceDetails.module.scss';

export interface DeviceDetailsProps {
	device: DeviceSummary | ConnectedDeviceSummary;
	onNameChange: (newName: string | undefined, id: string) => void;
}

const USAGE_UNIT = 'ML';
const MAX_TEXT_NAME_LIMIT = 100;

const deviceNameSchema = yup.object().shape({
	deviceName: yup
		.string()
		.max(MAX_TEXT_NAME_LIMIT, `Device name must be ${MAX_TEXT_NAME_LIMIT} characters or less`)
		.matches(displayNameRegExp, displayNameErrorMessage)
		.trim(),
});

export const DeviceDetails = ({ device, onNameChange }: DeviceDetailsProps) => {
	const { id, displayName, type: deviceType, reference } = device;

	// Context
	const { legalEntity } = useContext(LegalEntityContext);
	const {
		selectedDeviceReading: selectedReading,
		setSelectedDeviceReading,
		setSelectedDevice,
		showReadingModal,
		setShowReadingModal,
	} = useContext(ModalContext);

	// Displayer
	const [deviceDisplayer, setDeviceDisplayer] = useState(new DeviceDisplayController(device));
	const { labels: deviceLabels, visible: deviceVisible, values: deviceValues } = deviceDisplayer; // prettier-ignore

	// State
	const [DETAILS, READINGS, CONNECTIONS] = ['Details', 'Readings', 'Connected devices'];
	const [selectedTab, setSelectedTab] = useState(DETAILS);
	const [editMode, setEditMode] = useState(false);
	const [curName, setCurName] = useState(displayName);
	const [pageFilter, setPageFilter] = useState<PageFilter>(new PageFilter());
	const [showPaginator, setShowPaginator] = useState(false);

	const {
		control,
		watch,
		setValue,
		trigger,
		formState: { errors, isValid },
		getValues,
	} = useForm<{ deviceName?: string | undefined }>({
		defaultValues: {
			deviceName: displayName || '',
		},
		resolver:
			yupResolver(deviceNameSchema) as unknown as Resolver<{ deviceName?: string | undefined }>,
		mode: 'onChange', // Validate on change
	});

	// Data
	const getDeviceDetails = useQuery<DeviceType, Error>({
		queryKey: [CONSTANT.DEVICE_API_URL, legalEntity?.id, device.id],
		queryFn: async () => {
			if (!legalEntity || !device) throw new Error('Missing required data');
			return DeviceService.getDeviceDetails(legalEntity.id, device.id);
		},
		enabled: !!legalEntity?.id && !!device.id,
	});

	// Update displayer when device details are fetched
	useEffect(() => {
		if (getDeviceDetails.data) {
			setDeviceDisplayer(new DeviceDisplayController([device, getDeviceDetails.data]));
		}
	}, [getDeviceDetails.data, device]);

	const getReadings = useQuery({
		queryKey: [CONSTANT.READINGS_API_URL, legalEntity?.id, device.id, pageFilter.offSet],
		queryFn: async () => {
			if (!legalEntity || !device || !pageFilter) throw new Error('Missing required data');
			return DeviceReadingService.getDeviceReadings(legalEntity.id, device.id, pageFilter);
		},
		enabled:
			!!legalEntity?.id &&
			!!device.id &&
			pageFilter.offSet != undefined &&
			device.type != DeviceType.type.MODULAR_METER,
		placeholderData: keepPreviousData,
	});

	const updateDeviceMutation = useMutation({
		mutationFn: (payload: Partial<DeviceType>) =>
			DeviceService.updateDevice(legalEntity?.id ? legalEntity.id : '', payload),
	});

	// Memo
	const hasReadingsTab = useMemo(
		() =>
			![
				DeviceType.type.MODULAR_METER,
				DeviceType.type.WEIR_DISCHARGE,
				DeviceType.type.WL_STATION,
			].includes(deviceType as DeviceType.type),
		[deviceType],
	);

	const hasConnectedDevicesTab = useMemo(() => {
		const hasConnectedInParentDevice = !isEmpty((device as DeviceSummary).connectedDevices ?? []);
		if (hasConnectedInParentDevice) {
			return hasConnectedInParentDevice;
		}
		return (
			getDeviceDetails.isSuccess &&
			getDeviceDetails.data?.connectedDevices &&
			!isEmpty(getDeviceDetails.data.connectedDevices)
		);
	}, [device, getDeviceDetails.isSuccess, getDeviceDetails.data?.connectedDevices]);

	const availableTabs: string[] = useMemo(() => {
		const tabs = [DETAILS];
		if (hasReadingsTab) {
			tabs.push(READINGS);
		}
		if (hasConnectedDevicesTab) {
			tabs.push(CONNECTIONS);
		}
		return tabs;
	}, [DETAILS, READINGS, CONNECTIONS, hasReadingsTab, hasConnectedDevicesTab]);

	// Life Cycle
	useEffect(() => {
		setCurName(displayName);
		setValue('deviceName', displayName || '');
	}, [displayName, setValue]);

	useEffect(() => {
		resetEdit();
	}, [id, reference]);

	// On selected device
	useEffect(() => {
		setShowPaginator(false); // re-render Pagination component
		setPageFilter({ ...pageFilter, offSet: PageFilter.initialOffSet }); // reset pageFilter
	}, [id]); // eslint-disable-line react-hooks/exhaustive-deps

	// Reset selected tab on changed device
	useEffect(() => {
		setSelectedTab(DETAILS);
	}, [DETAILS, id]);

	// On first data fetched for selected device, finish re-render Pagination component
	useEffect(() => {
		if (getReadings.isFetchedAfterMount) {
			setShowPaginator(true); // re-render Pagination
		}
	}, [getReadings.isFetchedAfterMount]);

	// refreshes the readings when returning from the modal
	useEffect(() => {
		if (!showReadingModal) {
			getReadings.refetch();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [showReadingModal]);

	// Helpers
	const onPageChange = (selectedPage: number) =>
		handlePageChange(selectedPage, getReadings, setPageFilter);

	const onEdit = async () => {
		const formValue = watch('deviceName');

		// Trigger validation and check if form is valid
		const isFormValid = await trigger('deviceName');
		if (!isFormValid || !isValid) {
			return;
		}

		const data = { ...device, displayName: formValue ?? '' };
		if (curName !== formValue) {
			try {
				const res = await updateDeviceMutation.mutateAsync(data);
				if (res) {
					setCurName(formValue);
					onNameChange(formValue, id);
				}
			} catch (error) {
				// Handle offline/network errors
				console.error('Update failed:', error);
				return;
			}
		}
		resetEdit();
	};

	const resetEdit = () => {
		setEditMode(false);
	};

	const SwitchSelectedTabSection = () => {
		switch (selectedTab) {
			case DETAILS:
				return createDetailSection();
			case READINGS:
				return createReadingSection();
			case CONNECTIONS:
				return createConnectionsSection();
			default:
				return null;
		}
	};

	const SublistSkeleton = () => (
		<>
			{Array.from(Array(8)).map((_el, id) => {
				return (
					<tr key={`skeletion-${id}`}>
						<td>
							<Skeleton customStyles={styles['skeleton-sublist']} />
						</td>
						<td>
							<Skeleton customStyles={styles['skeleton-sublist']} />
						</td>
						<td>
							<Skeleton customStyles={styles['skeleton-sublist']} />
						</td>
						<td />
					</tr>
				);
			})}
		</>
	);

	const createReadingSection = () => {
		return (
			<>
				<div className={styles.sublist} data-testid="meterDetails">
					{(getReadings.isSuccess && isEmpty(getReadings.data?.items)) || getReadings.isError ? (
						<div className={styles['sublist-empty']}>
							<h6>
								No readings available or the portal failed to fetch readings, please try again
								later.
							</h6>
						</div>
					) : (
						<table>
							<thead className={styles['sublist-header']}>
								<tr>
									<th>Date</th>
									<th>Reading</th>
									<th>Usage since previous reading</th>
								</tr>
							</thead>
							<tbody>
								{getReadings.isLoading || getReadings.isFetching ? (
									<SublistSkeleton />
								) : (
									getReadings.data?.items.map((reading, id) => {
										return (
											<tr
												key={`reading-${id}`}
												onClick={() => {
													setSelectedDeviceReading(reading);
													setSelectedDevice(device);
													setShowReadingModal(true);
												}}
												className={[
													reading.status == ReadingStatus.WITHDRAWN
														? styles['sublist-row-strikethrough']
														: styles['sublist-row'],
													reading.id == selectedReading?.id
														? reading.status == ReadingStatus.WITHDRAWN
															? styles['sublist-row-strikethrough--selected']
															: styles['sublist-row--selected']
														: '',
												].join(' ')}
											>
												<td>{convertDateAndTimeToDisplayFormat(reading.readingDate)}</td>
												<td>{displayReadingValue(reading)}</td>
												<td>
													{hasAssessedTake(reading) ? (
														<AssessedTake value={reading.assessedTake?.toString()} />
													) : (
														displayVolumeOrStatus(reading.take, reading.status, USAGE_UNIT)
													)}
												</td>
											</tr>
										);
									})
								)}
							</tbody>
						</table>
					)}
				</div>
				{showPaginator && !isEmpty(getReadings.data) && !isEmpty(getReadings.data?.items) && (
					<Pagination
						itemCounts={getReadings.data.metadata.totalCount}
						paginationLimit={getReadings.data.metadata.limit}
						handleClick={onPageChange}
					/>
				)}
			</>
		);
	};

	const createConnectionsSection = () => {
		return (
			<>
				<div className={styles.sublist}>
					<table>
						<thead className={styles['sublist-header']}>
							<tr>
								<th>Reference</th>
								<th>Serial number</th>
								<th>Device type</th>
							</tr>
						</thead>
						<tbody>
							{getDeviceDetails.data?.connectedDevices?.map((connectedDevice, id) => {
								return (
									<tr key={`connection-${id}`} className={styles['sublist-row']}>
										<td>{connectedDevice.reference}</td>
										<td>{connectedDevice.serialNumber}</td>
										<td>{toSentenceCase(connectedDevice.deviceDescription ?? '') || '-'}</td>
									</tr>
								);
							})}
						</tbody>
					</table>
				</div>
			</>
		);
	};

	const createDetailSection = () => {
		const renderItem = [
			{
				header: deviceLabels.deviceDescription,
				show: deviceVisible.deviceDescription,
				content: deviceValues.deviceDescription,
			},
			{
				header: deviceLabels.make,
				show: deviceVisible.make,
				content: deviceValues.make,
			},
			{
				header: deviceLabels.model,
				show: deviceVisible.model,
				content: deviceValues.model,
			},
			{
				header: deviceLabels.serialNumber,
				show: deviceVisible.serialNumber,
				content: deviceValues.serialNumber,
				isLoading: getDeviceDetails.isLoading,
			},
			{
				header: deviceLabels.certificationExpiryDate,
				show: deviceVisible.certificationExpiryDate,
				content: deviceValues.certificationExpiryDate,
				isLoading: getDeviceDetails.isLoading,
			},
			{
				header: deviceLabels.size,
				show: deviceVisible.size,
				content: deviceValues.size,
				isLoading: getDeviceDetails.isLoading,
			},
			{
				header: deviceLabels.width,
				show: deviceVisible.width,
				content: deviceValues.width,
				isLoading: getDeviceDetails.isLoading,
			},
			{
				header: deviceLabels.widthUnit,
				show: deviceVisible.widthUnit,
				content: deviceValues.widthUnit,
				isLoading: getDeviceDetails.isLoading,
			},
			{
				header: deviceLabels.installationDate,
				show: deviceVisible.installationDate,
				content: deviceValues.installationDate,
				isLoading: getDeviceDetails.isLoading,
			},
			{
				header: deviceLabels.gpsLocation,
				show: deviceVisible.gpsLocation,
				content: deviceValues.gpsLocation,
				isLoading: getDeviceDetails.isLoading,
			},
			{
				header: deviceLabels.deviceStatus,
				show: deviceVisible.deviceStatus,
				content: deviceValues.deviceStatus,
			},
		];

		return (
			<div className={styles['detail-container']}>
				{renderItem
					.filter(item => item.show)
					.map(({ header, content, isLoading }) => {
						return (
							<Fragment key={header}>
								<h6>{header}</h6>
								{isLoading ? (
									<Skeleton customStyles={styles['skeleton-details']} />
								) : (
									<p>{content}</p>
								)}
							</Fragment>
						);
					})}
			</div>
		);
	};

	return (
		<>
			<div className={styles.container}>
				<div className={styles.title}>
					<h2>Device information</h2>
					<Meter customStyles={styles['title-icon']} />
				</div>
				<div className={styles['border-title']} />
				<div className={styles.brief}>
					{!editMode ? (
						<div
							className={styles.name}
							onClick={() => {
								setEditMode(true);
							}}
						>
							<div className={styles['name-text']}>
								{curName ? (
									<h3>{curName}</h3>
								) : (
									<h3 className={styles['name-text-placeholder']}>Edit device name</h3>
								)}
							</div>
							<Edit customStyles={styles['name-icon']} />
						</div>
					) : updateDeviceMutation.isPending ? (
						<div className={styles['edit-loader']}>
							<Loader size="medium" />
						</div>
					) : (
						<div className={styles.edit}>
							<ControllerPlus<{ deviceName?: string | undefined }>
								control={control}
								name="deviceName"
								schema={deviceNameSchema}
								labels={{ deviceName: 'Device Name' }}
								hideLabel={true}
								formValues={getValues()}
								isEditing={true}
								disabled={updateDeviceMutation.isPending}
							>
								<TextField
									placeholder="Please enter a name"
									errorMsg={errors.deviceName?.message}
									status={errors.deviceName ? 'error' : 'valid'}
									maxLength={MAX_TEXT_NAME_LIMIT}
								/>
							</ControllerPlus>
							<div className={styles['edit-controls']}>
								<Close
									customStyles={styles['edit-icon']}
									handleClick={() => {
										resetEdit();
									}}
								/>
								<Done
									customStyles={[
										styles['edit-icon'],
										(!isValid || updateDeviceMutation.isPending) && styles['edit-icon-disabled'],
									]
										.filter(Boolean)
										.join(' ')}
									handleClick={!isValid || updateDeviceMutation.isPending ? undefined : onEdit}
								/>
							</div>
						</div>
					)}
					<p className={styles.ref}>Reference: {reference}</p>
				</div>
				<div className={styles.tabs}>
					<Tabs
						tabs={availableTabs}
						handleClick={name => setSelectedTab(name)}
						selectedTab={selectedTab}
					/>
					<div className={styles['border-tab']} />
				</div>
				<div className={styles['sublist-container']}>{SwitchSelectedTabSection()}</div>
			</div>
			{updateDeviceMutation.error && (
				<NotificationToast
					show={!!updateDeviceMutation.error}
					itemType="device-update-error"
					position="bottom-left"
					autoDelete={true}
				/>
			)}
		</>
	);
};
